// Unmatched-deposits API — typed function over the admin backend contract.
// Consumed verbatim; no invented fields.
//
//   GET /admin/deposits/unmatched
//     -> { deposits: [{ id, chain, address, amount, tx_hash, block_time,
//                       reason, seen_at }] }
//
// These are on-chain deposits the server-side listener saw land on an address
// that matched NO provisioned client wallet ("unmatched") or that arrived
// before its address's provisioning window ("skipped_predates"). This is real
// money-in that hit no client ledger, so ops must see + manually resolve it.
//
// SECURITY: read-only, NoStore, sanitized server-side. This UI never holds any
// master credential — it only DISPLAYS what the admin backend returns
// (invariants 1/3). Amounts are exact decimal STRINGS (never floats).

import { api } from "./client";

// Why a matched deposit was NOT credited to a client:
//   unmatched        — no provisioned (chain, address) wallet owns this address.
//   skipped_predates — the deposit predates its address's provisioning window
//                      (deliberately skipped, not lost).
export type UnmatchedDepositReason = "unmatched" | "skipped_predates";

// One unmatched deposit row. `chain` is "tron" | "eth" on the wire but kept a
// plain string so an unexpected chain never breaks rendering. Money is a decimal
// string; block_time / seen_at are RFC3339 UTC strings (block_time "" when unknown).
export interface UnmatchedDeposit {
  id: string;
  chain: string;
  address: string;
  amount: string;
  tx_hash: string;
  // On-chain block time, RFC3339 UTC string; "" (or absent) when unknown.
  block_time?: string;
  reason: UnmatchedDepositReason;
  // When the listener observed the deposit, RFC3339 UTC string (always present).
  seen_at: string;
}

export interface UnmatchedDepositList {
  deposits: UnmatchedDeposit[];
}

// Fetch all currently-unresolved incoming deposits that matched no provisioned
// address. Read-only; the backend sanitizes + NoStores the response.
export function getUnmatchedDeposits(): Promise<UnmatchedDepositList> {
  return api.get<UnmatchedDepositList>("/admin/deposits/unmatched");
}
